import {
  buildMercureUrl,
  getMercureToken,
  invalidateMercureToken,
} from "@/lib/mercure";
import { Notification as NotificationType } from "@/redux/services/notification/notification.type";
import { logger } from "@/services/Logger";

class NotificationService {
  private eventSource: EventSource | null = null;
  private onNotificationCallback:
    | ((notification: NotificationType) => void)
    | null = null;
  private connectionAttempts = 0;
  private readonly maxRetries = 10; // Increased from 3
  private readonly reconnectDelay = 1000; // Reduced to 1 second for faster recovery
  private reconnectTimer: NodeJS.Timeout | null = null;
  private healthCheckTimer: NodeJS.Timeout | null = null;
  private readonly healthCheckInterval = 5000; // Aggressive 5 second health checks
  private lastConnectionTime = 0;
  private lastActivityTime = 0; // Track last activity (message or heartbeat)
  private connectionParams: {
    restaurantId: string;
    userRole: string;
  } | null = null;
  private isReconnecting = false;
  private visibilityHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;
  private reconnectDebounceTimer: NodeJS.Timeout | null = null;
  private readonly reconnectDebounceDelay = 500; // Reduced to 500ms for faster recovery
  private serverHeartbeatTimer: NodeJS.Timeout | null = null;
  private readonly serverHeartbeatTimeout = 30000; // Reduced to 30 seconds for faster dead connection detection
  private connectionQualityTimer: NodeJS.Timeout | null = null;
  private missedPings = 0;
  // Bumped by every connect() and disconnect(): a token fetch that resolves
  // after a newer attempt (or a disconnect) must not open a connection.
  private connectSeq = 0;

  init(
    restaurantId: string,
    userRole: string,
    onNotification: (notification: NotificationType) => void
  ) {
    this.onNotificationCallback = onNotification;
    this.connectionParams = { restaurantId, userRole };

    // Set up visibility and network change listeners
    this.setupEventListeners();

    // Connect
    this.connect();
  }

  private connect() {
    if (!this.connectionParams || this.isReconnecting) {
      return;
    }

    const attempt = ++this.connectSeq;
    const { restaurantId } = this.connectionParams;

    // The hub needs a subscriber JWT scoped to this restaurant (SFAPI-121).
    void getMercureToken(restaurantId).then((token) => {
      if (attempt !== this.connectSeq || !this.connectionParams) {
        return;
      }
      this.openEventSource(token);
    });
  }

  private openEventSource(token: string | null) {
    if (!this.connectionParams) {
      return;
    }

    const { restaurantId, userRole } = this.connectionParams;
    const topic = `/restaurant/${restaurantId}/${userRole}/notifications`;

    try {
      // Close any existing connection first
      this.closeConnection();

      this.eventSource = new EventSource(buildMercureUrl(topic, token));
      this.lastConnectionTime = Date.now();

      // Log connection status
      this.eventSource.onopen = () => {
        logger.info(
          "NotificationService",
          "Mercure notification connection established"
        );
        this.connectionAttempts = 0; // Reset counter on successful connection
        this.isReconnecting = false;
        this.lastActivityTime = Date.now();
        this.missedPings = 0;
        this.startHealthCheck();
        this.startServerHeartbeatMonitor();
        this.startConnectionQualityMonitor();
      };

      this.eventSource.onerror = (err) => {
        logger.error(
          "NotificationService",
          "Mercure notification connection error:",
          err
        );
        // The hub may have refused the token: fetch a new one next time.
        invalidateMercureToken(restaurantId);
        this.handleConnectionError();
      };

      this.eventSource.onmessage = (event) => {
        this.lastActivityTime = Date.now(); // Update activity time on any message

        try {
          const data = JSON.parse(event.data);

          // Check if this is a heartbeat message from server
          if (data.type === "heartbeat") {
            logger.debug("NotificationService", "Received server heartbeat");
            return;
          }

          // Otherwise, treat as notification
          const notification = data as NotificationType;
          this.onNotificationCallback?.(notification);
        } catch (error) {
          logger.error(
            "NotificationService",
            "Error processing notification:",
            error
          );
        }
      };
    } catch (error) {
      logger.error(
        "NotificationService",
        "Failed to initialize notification service:",
        error
      );
      this.handleConnectionError();
    }
  }

  private setupEventListeners() {
    // Remove existing listeners if any
    this.removeEventListeners();

    // Visibility change handler
    this.visibilityHandler = () => {
      if (document.visibilityState === "visible") {
        logger.info(
          "NotificationService",
          "App became visible, checking connection..."
        );
        this.handleAppForeground();
      } else {
        logger.info(
          "NotificationService",
          "App went to background - keeping connection alive"
        );
        // Don't stop health checks - keep everything running to maintain connection
      }
    };

    // Online/offline handlers
    this.onlineHandler = () => {
      logger.info("NotificationService", "Network connection restored");
      this.debouncedReconnect();
    };

    this.offlineHandler = () => {
      logger.info("NotificationService", "Network connection lost");
      this.closeConnection();
    };

    // Add event listeners
    document.addEventListener("visibilitychange", this.visibilityHandler);
    window.addEventListener("online", this.onlineHandler);
    window.addEventListener("offline", this.offlineHandler);
  }

  private removeEventListeners() {
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
    if (this.onlineHandler) {
      window.removeEventListener("online", this.onlineHandler);
    }
    if (this.offlineHandler) {
      window.removeEventListener("offline", this.offlineHandler);
    }
  }

  private handleAppForeground() {
    // Check if connection is still valid
    if (
      !this.isConnected() ||
      this.isConnectionStale() ||
      this.isServerConnectionDead()
    ) {
      logger.info(
        "NotificationService",
        "Connection lost, stale, or dead, reconnecting..."
      );
      this.debouncedReconnect();
    } else {
      // Resume health check and server heartbeat monitoring
      this.startHealthCheck();
      this.startServerHeartbeatMonitor();
    }
  }

  private isConnectionStale(): boolean {
    // Consider connection stale if it's been more than 5 minutes since last connection
    const staleThreshold = 5 * 60 * 1000; // 5 minutes
    return Date.now() - this.lastConnectionTime > staleThreshold;
  }

  private startHealthCheck() {
    this.stopHealthCheck();

    this.healthCheckTimer = setInterval(() => {
      if (!this.isConnected()) {
        logger.info(
          "NotificationService",
          "Health check failed, reconnecting..."
        );
        this.debouncedReconnect();
      } else if (this.isServerConnectionDead()) {
        logger.info(
          "NotificationService",
          "Server connection appears dead (no activity), reconnecting..."
        );
        this.debouncedReconnect();
      }
    }, this.healthCheckInterval);
  }

  private stopHealthCheck() {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  private startServerHeartbeatMonitor() {
    this.stopServerHeartbeatMonitor();

    this.serverHeartbeatTimer = setInterval(() => {
      if (this.isServerConnectionDead()) {
        logger.info(
          "NotificationService",
          "Server heartbeat timeout, connection appears dead"
        );
        this.immediateReconnect(); // Use immediate reconnect for critical situations
      }
    }, this.serverHeartbeatTimeout / 3); // Check every 10 seconds
  }

  private stopServerHeartbeatMonitor() {
    if (this.serverHeartbeatTimer) {
      clearInterval(this.serverHeartbeatTimer);
      this.serverHeartbeatTimer = null;
    }
  }

  private isServerConnectionDead(): boolean {
    // Consider connection dead if no activity for the timeout period
    return Date.now() - this.lastActivityTime > this.serverHeartbeatTimeout;
  }

  private debouncedReconnect() {
    // Clear any existing debounce timer
    if (this.reconnectDebounceTimer) {
      clearTimeout(this.reconnectDebounceTimer);
    }

    // Set new debounce timer
    this.reconnectDebounceTimer = setTimeout(() => {
      this.reconnect();
      this.reconnectDebounceTimer = null;
    }, this.reconnectDebounceDelay);
  }

  private immediateReconnect() {
    logger.info(
      "NotificationService",
      "Immediate reconnection triggered - critical connection issue"
    );
    // Cancel any pending reconnections
    this.clearReconnectTimer();
    if (this.reconnectDebounceTimer) {
      clearTimeout(this.reconnectDebounceTimer);
      this.reconnectDebounceTimer = null;
    }
    // Reconnect immediately
    this.connectionAttempts = 0; // Reset to ensure fast reconnection
    this.connect();
  }

  private startConnectionQualityMonitor() {
    this.stopConnectionQualityMonitor();

    // Monitor connection quality every 3 seconds
    this.connectionQualityTimer = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastActivityTime;

      // If no activity for more than 15 seconds, send a ping or reconnect
      if (timeSinceLastActivity > 15000) {
        this.missedPings++;
        logger.info(
          "NotificationService",
          `Connection quality degraded - no activity for ${timeSinceLastActivity}ms, missed pings: ${this.missedPings}`
        );

        // After 2 missed pings, force reconnection
        if (this.missedPings >= 2) {
          logger.info(
            "NotificationService",
            "Too many missed pings, forcing reconnection"
          );
          this.immediateReconnect();
        }
      } else {
        // Reset missed pings if we have recent activity
        this.missedPings = 0;
      }
    }, 3000);
  }

  private stopConnectionQualityMonitor() {
    if (this.connectionQualityTimer) {
      clearInterval(this.connectionQualityTimer);
      this.connectionQualityTimer = null;
    }
  }

  private handleConnectionError() {
    this.connectionAttempts++;

    // Circuit breaker pattern - if too many failures, back off exponentially
    const isCircuitOpen = this.connectionAttempts > this.maxRetries * 2;

    if (this.connectionAttempts <= this.maxRetries) {
      const delay = Math.min(
        this.reconnectDelay * Math.pow(1.2, this.connectionAttempts - 1), // Less aggressive backoff
        10000 // Max 10 seconds for critical notifications
      );

      logger.info(
        "NotificationService",
        `Attempting to reconnect (${this.connectionAttempts}/${this.maxRetries}) in ${delay}ms...`
      );

      this.scheduleReconnect(delay);
    } else if (isCircuitOpen) {
      // Circuit is open - but still try more frequently for critical notifications
      logger.error(
        "NotificationService",
        `Circuit breaker open after ${this.connectionAttempts} attempts. Waiting 1 minute...`
      );
      this.connectionAttempts = 0; // Reset for next round
      this.scheduleReconnect(60000); // Try again in 1 minute
    } else {
      logger.error(
        "NotificationService",
        `Failed to connect after ${this.maxRetries} attempts`
      );
      // Continue trying with moderate delays
      const backoffDelay = Math.min(
        30000 * (this.connectionAttempts - this.maxRetries),
        60000
      ); // Max 1 minute
      this.scheduleReconnect(backoffDelay);
    }
  }

  private scheduleReconnect(delay: number) {
    this.clearReconnectTimer();
    this.isReconnecting = true;

    this.reconnectTimer = setTimeout(() => {
      this.isReconnecting = false;
      this.connect();
    }, delay);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private closeConnection() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.stopHealthCheck();
    this.stopServerHeartbeatMonitor();
    this.stopConnectionQualityMonitor();
    this.clearReconnectTimer();
    if (this.reconnectDebounceTimer) {
      clearTimeout(this.reconnectDebounceTimer);
      this.reconnectDebounceTimer = null;
    }
  }

  reconnect() {
    if (!this.connectionParams || this.isReconnecting) {
      return;
    }

    logger.info("NotificationService", "Manual reconnection triggered");
    this.connectionAttempts = 0; // Reset attempts for manual reconnection
    this.connect();
  }

  disconnect() {
    this.connectSeq++;
    this.closeConnection();
    this.removeEventListeners();
    this.connectionParams = null;
    this.onNotificationCallback = null;
    this.isReconnecting = false;
  }

  // Check if we're currently connected
  isConnected(): boolean {
    return (
      this.eventSource !== null &&
      this.eventSource.readyState === EventSource.OPEN
    );
  }

  // Get connection status
  getConnectionStatus(): {
    connected: boolean;
    attempts: number;
    lastConnectionTime: number;
    lastActivityTime: number;
    isServerConnectionAlive: boolean;
  } {
    return {
      connected: this.isConnected(),
      attempts: this.connectionAttempts,
      lastConnectionTime: this.lastConnectionTime,
      lastActivityTime: this.lastActivityTime,
      isServerConnectionAlive: !this.isServerConnectionDead(),
    };
  }

  // Force reset connection - useful for debugging or manual intervention
  forceReset() {
    logger.info(
      "NotificationService",
      "Force resetting notification service connection"
    );
    this.connectionAttempts = 0;
    this.closeConnection();
    if (this.connectionParams) {
      setTimeout(() => this.connect(), 1000);
    }
  }
}

export const notificationService = new NotificationService();
